// Helper functions for generating Mapping of Impact to SDGs chart data

import { CATEGORICAL_IMPACT } from '../fields'
import { getCategoricalLabels } from '../SocialValueByStakeholders/socialValueByStakeholdersHelpers'

// Full SDG names (1-17)
export const sdgNames = {
  1: 'No Poverty',
  2: 'Zero Hunger',
  3: 'Good Health and Well-Being',
  4: 'Quality Education',
  5: 'Gender Equality',
  6: 'Clean Water and Sanitation',
  7: 'Affordable and Clean Energy',
  8: 'Decent Work and Economic Growth',
  9: 'Industry, Innovation and Infrastructure',
  10: 'Reduced Inequalities',
  11: 'Sustainable Cities and Communities',
  12: 'Responsible Consumption and Production',
  13: 'Climate Action',
  14: 'Life Below Water',
  15: 'Life on Land',
  16: 'Peace, Justice and Strong Institutions',
  17: 'Partnerships for the Goals'
}

// Official SDG colors (1-17)
const sdgColors = {
  1: '#E5243B',
  2: '#DDA63A',
  3: '#4C9F38',
  4: '#C5192D',
  5: '#FF3A21',
  6: '#26BDE2',
  7: '#FCC30B',
  8: '#A21942',
  9: '#FD6925',
  10: '#DD1367',
  11: '#FD9D24',
  12: '#BF8B2E',
  13: '#3F7E44',
  14: '#0A97D9',
  15: '#56C02B',
  16: '#00689D',
  17: '#19486A'
}

// Extract numeric SDG number from key (e.g., "sdg_3" -> 3)
const extractSdgNumber = sdgKey => {
  if (typeof sdgKey === 'number') return sdgKey
  if (typeof sdgKey === 'string' && sdgKey.startsWith('sdg_')) {
    return Number(sdgKey.replace('sdg_', ''))
  }
  return Number(sdgKey)
}

// Generate chart data for SDG attribution
const generateSdgAttributionChartData = sdgAttributionData => {
  if (sdgAttributionData) {
    // Get SDGs that have values (filter out those with 0 or null values)
    const activeSdgs = Object.keys(sdgAttributionData)
      .filter(sdg => {
        const value = sdgAttributionData[sdg]
        return value && value > 0
      })
      .sort((a, b) => extractSdgNumber(a) - extractSdgNumber(b)) // Sort numerically

    if (activeSdgs.length === 0) {
      return {
        labels: [],
        datasets: []
      }
    }

    const labels = activeSdgs.map(sdg => `SDG ${extractSdgNumber(sdg)}`)
    const data = activeSdgs.map(sdg => sdgAttributionData[sdg])
    const colors = activeSdgs.map(sdg => {
      const sdgNumber = extractSdgNumber(sdg)
      // Cycle through colors 1-17 for SDGs beyond 17
      const colorKey = ((sdgNumber - 1) % 17) + 1
      return sdgColors[colorKey]
    })

    return {
      labels: labels,
      datasets: [
        {
          label: 'Mapping of Impact to SDGs',
          data: data,
          backgroundColor: colors,
          borderRadius: 4,
          maxBarThickness: 55
        }
      ]
    }
  } else {
    return {
      labels: [],
      datasets: []
    }
  }
}

// Generate SDG chart data from report
export const generateSdgMappingData = ({ report, selectedStakeholder }) => {
  // If stakeholder filter is applied, use nested sdg_attribution from categorical_impact[i]
  if (selectedStakeholder) {
    // Get labels to find the selected stakeholder index
    const allCategoricalImpact = report?.[CATEGORICAL_IMPACT] || []
    const labels = getCategoricalLabels(allCategoricalImpact)
    const selectedIndex = labels.findIndex(label => label === selectedStakeholder)

    if (selectedIndex !== -1) {
      const stakeholderData = allCategoricalImpact[selectedIndex]
      const sdgAttributionData = stakeholderData?.sdg_attribution
      return generateSdgAttributionChartData(sdgAttributionData)
    }

    return {
      labels: [],
      datasets: []
    }
  }

  // Otherwise, use top-level sdg_attribution
  const sdgAttributionData = report?.sdg_attribution
  return generateSdgAttributionChartData(sdgAttributionData)
}
